<div class="device-health">
  @if (hasFilterBar()) {
    <div class="device-health-summary flex-row start-center gap-8">
      <div class="flex-row start-center gap-16">
        <div class="flex-row start-center gap-6">
          <span class="ui-icon-status-online"></span>
          <span class="status-counter">{{ counts().online }}</span>
        </div>
        <div class="flex-row start-center gap-6">
          <span class="ui-icon-status-unhealthy"></span>
          <span class="status-counter">{{ counts().unhealthy }}</span>
        </div>
        <div class="flex-row start-center gap-6">
          <span class="ui-icon-status-offline"></span>
          <span class="status-counter">{{ counts().offline }}</span>
        </div>
        @if (counts().installing) {
          <!-- Installing shares the unhealthy glyph (web ui-device-status parity),
               so the tooltip is what tells the two counters apart. -->
          <div
            class="flex-row start-center gap-6"
            [matTooltip]="'DEVICE_STATUS.INSTALLING' | translate"
          >
            <span class="ui-icon-status-unhealthy"></span>
            <span class="status-counter">{{ counts().installing }}</span>
          </div>
        }
      </div>
    </div>

    <!-- The device-list page's filter bar, over the response's own facets: the
         tool result seeds these controls and every later change refetches. -->
    <div class="device-health-filters flex-row start-center gap-8">
      <ui-search
        class="filter-search"
        [searchQuery]="formFilters().name ?? ''"
        [placeholder]="'DEVICE_LIST.PLACEHOLDERS.SEARCH' | translate"
        (searchQueryChange)="onSearchChanged($event)"
      ></ui-search>

      <mat-select
        class="ui-selector filter-select"
        panelClass="selector-option-full-width"
        [formControl]="filtersForm.controls.types"
        [multiple]="true"
        [disableOptionCentering]="true"
        [placeholder]="'DEVICE_LIST.PLACEHOLDERS.DEVICE_TYPE' | translate"
        (closed)="commitFilters()"
      >
        @for (option of typeOptions; track option.value) {
          <mat-option [value]="option.value">{{ option.labelKey | translate }}</mat-option>
        }
      </mat-select>

      <mat-select
        class="ui-selector filter-select"
        panelClass="selector-option-full-width"
        [formControl]="filtersForm.controls.statuses"
        [multiple]="true"
        [disableOptionCentering]="true"
        [placeholder]="'COMMON.STATUS' | translate"
        (closed)="commitFilters()"
      >
        @for (option of statusOptions; track option.value) {
          <mat-option [value]="option.value">{{ option.labelKey | translate }}</mat-option>
        }
      </mat-select>

      @if (showLocationFilter()) {
        <!-- Submits location IDS. A prompt-seeded partial location NAME shows no
             selection here while its chip still shows it — intended. -->
        <mat-select
          class="ui-selector filter-select"
          panelClass="selector-option-full-width"
          [formControl]="filtersForm.controls.locations"
          [multiple]="true"
          [disableOptionCentering]="true"
          [placeholder]="'DEVICE_HEALTH.LOCATION' | translate"
          (closed)="commitFilters()"
        >
          @for (option of locationOptions(); track option.id) {
            <mat-option [value]="option.id">{{ option.name }}</mat-option>
          }
        </mat-select>
      }

      @if (showTagFilter()) {
        <mat-select
          class="ui-selector filter-select"
          panelClass="selector-option-full-width"
          [formControl]="filtersForm.controls.tagIds"
          [multiple]="true"
          [disableOptionCentering]="true"
          [placeholder]="'DEVICE_LIST.PLACEHOLDERS.TAGS_FILTERS' | translate"
          (closed)="commitFilters()"
        >
          @for (option of tagOptions(); track option.id) {
            <mat-option [value]="option.id">{{ option.name }}</mat-option>
          }
        </mat-select>
      }

      <mat-select
        class="ui-selector filter-select"
        panelClass="selector-option-full-width"
        [formControl]="filtersForm.controls.advanced"
        [multiple]="true"
        [disableOptionCentering]="true"
        [placeholder]="'DEVICE_LIST.PLACEHOLDERS.ADVANCED_FILTERS' | translate"
        (closed)="commitFilters()"
      >
        @for (option of advancedOptions; track option.value) {
          <mat-option [value]="option.value">{{ option.labelKey | translate }}</mat-option>
        }
      </mat-select>
    </div>

    @if (filterChips().length) {
      <div class="device-health-chips flex-row start-center gap-8">
        <ui-button
          type="noBorderDefault"
          size="s"
          iconClass="ui-icon-cross"
          [matTooltip]="'DEVICE_LIST.TOOLTIPS.CLEAR_FILTERS' | translate"
          (clickAction)="clearAllFilters()"
        ></ui-button>
        @for (chip of filterChips(); track chip.kind + ':' + chip.value) {
          @let chipValue = chip.valueLabelKey ? (chip.valueLabelKey | translate) : chip.valueLabel;
          <ui-filter-chip
            [title]="(chip.labelKey | translate) + ': ' + chipValue"
            (removeClicked)="removeFilterChip(chip)"
          ></ui-filter-chip>
        }
      </div>
    }
  }

  @if (refetchFailed()) {
    <div class="device-health-error">{{ 'SOMETHING_WENT_WRONG.TITLE' | translate }}</div>
  }

  @if (refetching()) {
    <div class="device-health-loading flex-row center-center">
      <ui-preloader [color]="PreloaderColor.gray" [diameter]="32"></ui-preloader>
    </div>
  } @else {
    @if (!items().length) {
      <div class="device-health-empty">{{ 'DEVICE_HEALTH.EMPTY' | translate }}</div>
    }

    <ui-device-list
      [items]="items()"
      [getItemKey]="rowKey"
      [estimateSize]="estimateRowSize"
      [itemTemplate]="rowTemplate"
      [paddingEnd]="paddingEnd()"
    >
      <ng-template #rowTemplate let-item>
        <div class="device-health-entry flex-col">
          <div
            class="device-health-row flex-row start-center gap-16"
            [class.selected]="isCameraSelected(item.id)"
            [class.expanded]="isExpanded(item)"
            (click)="toggleRow(item)"
          >
            <div class="row-lead flex-row start-center gap-16">
              @if (isCamera(item)) {
                <mat-checkbox
                  (change)="selectCamera(item.id, $event)"
                  (click)="$event.stopPropagation()"
                  [checked]="isCameraSelected(item.id)"
                ></mat-checkbox>
                <ui-camera-status-snapshot
                  [cameraId]="item.id"
                  [iconSizePx]="20"
                  [showStatus]="false"
                ></ui-camera-status-snapshot>
              } @else {
                <span class="checkbox-space"></span>
                <img class="core-image" src="assets/devices/core.png" alt="" />
              }
            </div>
            <span
              class="row-status-icon"
              [class]="statusIconClass(item.status)"
              [attr.aria-label]="statusLabelKey(item.status) | translate"
              [matTooltip]="statusLabelKey(item.status) | translate"
            ></span>
            <div class="row-name flex-row start-center gap-8">
              <span class="truncate">{{ item.name }}</span>
              @if (item.issueCount) {
                <span class="row-issues">
                  {{ issueLabelKey(item) | translate: issueLabelParams(item) }}
                </span>
              }
            </div>
            <div class="row-type truncate">{{ typeLabelKey(item) | translate }}</div>
            <div class="row-location flex-row start-center gap-6">
              <span class="ui-icon-location"></span>
              <span class="truncate">{{ item.locationName }}</span>
            </div>
            <span
              class="row-chevron ui-icon-arrow-down"
              [class.open]="isExpanded(item)"
              [attr.aria-label]="'LOCATION_LIST.CLICK_FOR_DETAILS' | translate"
            ></span>
          </div>

          @if (isExpanded(item)) {
            <div
              class="device-health-details flex-col gap-8"
              [class.device-health-details-plain]="isLoadingDetails(item) || hasFailedDetails(item)"
            >
              @if (isLoadingDetails(item)) {
                <div class="details-state flex-row center-center">
                  <ui-preloader [color]="PreloaderColor.gray" [diameter]="24"></ui-preloader>
                </div>
              } @else if (hasFailedDetails(item)) {
                <div class="details-state flex-row center-center">
                  {{ 'DEVICE_HEALTH.DETAILS_ERROR' | translate }}
                </div>
              } @else {
                <div class="health-chips">
                  @for (chip of healthChips(item); track chip.labelKey) {
                    <span
                      class="health-chip flex-row start-center gap-6"
                      [class]="chipToneClass(chip.tone)"
                      [matTooltip]="
                        chip.tooltipKey
                          ? (chip.tooltipKey | translate: chip.tooltipParams)
                          : (chip.tooltip ?? '')
                      "
                    >
                      <span class="health-chip-icon" [class]="chipIconClass(chip)"></span>
                      <span class="truncate">{{ chip.labelKey | translate }}</span>
                      @for (mediaNote of chip.media ?? []; track mediaNote) {
                        <span class="health-chip-media">{{ mediaNote }}</span>
                      }
                      @if (chip.note) {
                        <span class="health-chip-note truncate">{{ chip.note }}</span>
                      }
                    </span>
                  }
                </div>

                @if (cameraPing(item); as ping) {
                  <!-- The device-list dialog's "Ping (ms)" row
                       (ui-camera-status.component.html:113-133). -->
                  <div class="health-ping flex-row start-center gap-8">
                    <span class="health-ping-icon" [class]="pingIconClass(ping)"></span>
                    <span class="health-ping-label">{{ 'LOCATION_LIST.PING_MS' | translate }}</span>
                    <div class="flex-row start-center gap-8">
                      <span class="health-ping-value">
                        {{ 'LOCATION_LIST.PING_AVG' | translate }} {{ ping.avg }}
                      </span>
                      <span class="health-ping-value">
                        {{ 'LOCATION_LIST.PING_MAX' | translate }} {{ ping.max }}
                      </span>
                      <span class="health-ping-value">
                        {{ 'LOCATION_LIST.PING_MIN' | translate }} {{ ping.min }}
                      </span>
                      <span class="health-ping-value">
                        {{ 'LOCATION_LIST.PING_MDEV' | translate }} {{ ping.mdev }}
                      </span>
                    </div>
                  </div>
                }

                @if (metrics(item).length) {
                  <div class="health-metrics">
                    @for (metric of metrics(item); track metric.labelKey) {
                      <div class="health-metric" [class.health-metric-wide]="metric.wide">
                        <div class="health-metric-label truncate">{{ metric.labelKey | translate }}</div>
                        <div class="health-metric-value truncate">
                          @if (metric.valueKey) {
                            {{ metric.valueKey | translate }}
                          } @else if (metric.date) {
                            <ui-user-settings-date
                              [date]="metric.date"
                              [dateTypeFormat]="dateTypeFormat"
                              [timezone]="metric.dateTimezone"
                              [showTimeZone]="true"
                            ></ui-user-settings-date>
                          } @else {
                            {{ metric.value ?? '—' }}
                          }
                        </div>
                      </div>
                    }
                  </div>
                }

                @if (coreNetwork(item).length) {
                  <div class="health-section-label">{{ 'LOCATION_LIST.NETWORK_INFO' | translate }}</div>
                  <div class="health-network">
                    @for (netInterface of coreNetwork(item); track netInterface.name) {
                      <div class="network-card flex-col gap-8">
                        <div class="network-card-head flex-row start-center gap-8">
                          <span class="network-card-title truncate">{{ netInterface.name }}</span>
                          @if (hasLinkSpeed(netInterface)) {
                            <span class="network-speed">{{ linkSpeed(netInterface) }}</span>
                          }
                        </div>
                        <div class="network-fields">
                          <div class="health-metric">
                            <div class="health-metric-label truncate">
                              {{ 'LOCATION_LIST.IP_ADDRESS' | translate }}
                            </div>
                            <div class="health-metric-value health-metric-mono truncate">
                              {{ netInterface.ip ?? '—' }}
                            </div>
                          </div>
                          <div class="health-metric">
                            <div class="health-metric-label truncate">
                              {{ 'LOCATION_LIST.MAC_ADDRESS' | translate }}
                            </div>
                            <div class="health-metric-value health-metric-mono truncate">
                              {{ netInterface.mac ?? '—' }}
                            </div>
                          </div>
                          <div class="health-metric">
                            <div class="health-metric-label truncate">
                              {{ 'LOCATION_LIST.LINK_SPEED' | translate }}
                            </div>
                            <div class="health-metric-value truncate">
                              {{ rawLinkSpeed(netInterface) }}
                            </div>
                          </div>
                          <div class="health-metric">
                            <div class="health-metric-label truncate">
                              {{ 'LOCATION_LIST.CABLE_CONNECTED' | translate }}
                            </div>
                            <div class="health-metric-value network-cable-state">
                              @if (netInterface.cableConnected === true) {
                                <span class="ui-icon-connected network-cable-connected"></span>
                              } @else if (netInterface.cableConnected === false) {
                                <span class="ui-icon-disconnected network-cable-disconnected"></span>
                              } @else {
                                —
                              }
                            </div>
                          </div>
                        </div>
                      </div>
                    }
                  </div>
                }

                <div class="details-actions flex-row end-center">
                  <ui-button
                    iconClass="ui-icon-reset"
                    type="default"
                    size="s"
                    [loading]="isRefreshingDetails(item)"
                    [disabled]="isRefreshingDetails(item)"
                    [preloaderColor]="PreloaderColor.gray"
                    (clickAction)="refreshRow(item)"
                  ></ui-button>
                </div>
              }
            </div>
          }
        </div>
      </ng-template>
    </ui-device-list>
  }

  @if (selectedCount()) {
    <div class="camera-selection flex-row start-center">
      <div class="camera-counter flex-row center-center">
        {{ selectedCount() }}
      </div>
      <div class="camera-actions flex-row start-center w-100 gap-8">
        <div class="selection-content flex-row space-between center w-100">
          <div class="cameras-selected-label flex-row start-center">
            {{ 'LOCATION_LIST.CAMERAS_SELECTED' | translate }}
          </div>
          <div class="selection-actions flex-row start-center gap-8">
            <ui-button type="noBorderPrimary" (clickAction)="selectAllCameras()">
              {{ 'COMMON.SELECT_ALL' | translate }}
            </ui-button>
            @if (enableChangePasswordFeature()) {
              <ui-button type="secondary" (clickAction)="changePasswordSelected()">
                <div class="flex-row start-center gap-8">
                  <span class="ui-icon-key select-cameras-btn-icon"></span>
                  {{ 'LOCATION_LIST.CHANGE_PASSWORD' | translate }}
                </div>
              </ui-button>
            }
            <ui-button type="secondary" (clickAction)="testSelected()">
              <div class="flex-row start-center gap-8">
                <span class="ui-icon-object-edge select-cameras-btn-icon"></span>
                {{ 'LOCATION_LIST.TEST_SELECTED' | translate }}
              </div>
            </ui-button>
            <ui-button type="secondary" (clickAction)="manageSelected()">
              <div class="flex-row start-center gap-8">
                <span class="ui-icon-edit select-cameras-btn-icon"></span>
                {{ 'LOCATION_LIST.MANAGE_SELECTED' | translate }}
              </div>
            </ui-button>
          </div>
        </div>
        <div class="horizontal-divider"></div>
        <div class="close-wrapper flex-row center-center">
          <span class="ui-icon-close ui-icon-button no-border" (click)="clearSelectedCameras()"></span>
        </div>
      </div>
    </div>
  }
</div>
